import type { JSX } from 'react'

/**
 * A deployment's state, readable without colour.
 *
 * The criterion is "state encoded in form, not colour alone", and the reason is
 * practical rather than decorative: this list is read by people with colour
 * vision deficiency, on a projector in a meeting, and pasted as a screenshot
 * into a ticket. So each status carries its own word *and* its own shape, and
 * colour is the third signal rather than the only one.
 *
 * The words are the API's, unchanged. Translating `apply-failed` into something
 * friendlier here would mean two vocabularies for the same fact — the one in the
 * console and the one in a log — and someone reconciling them under pressure.
 */

const SHAPES: Readonly<Record<string, { readonly glyph: string; readonly label: string }>> = {
  live: { glyph: '●', label: 'Live' },
  'in-progress': { glyph: '◐', label: 'In progress' },
  'apply-failed': { glyph: '▲', label: 'Apply failed' },
  destroyed: { glyph: '○', label: 'Destroyed' },
  'never-applied': { glyph: '◌', label: 'Never applied' },
}

export const DeploymentStatus = ({ status }: { status: string }): JSX.Element => {
  const shape = SHAPES[status]
  return (
    <span className={`status status-${status}`}>
      {/* Decorative: the word beside it is the accessible name, so a screen
          reader is not made to announce a glyph. */}
      <span aria-hidden="true">{shape?.glyph ?? '◌'}</span>{' '}
      {/* An unknown status shows its raw value rather than a friendly guess: a
          console that silently renders a state it does not know as "Unknown" is
          how a new state goes unnoticed for a release. */}
      {shape?.label ?? status}
    </span>
  )
}

const RUN_STATES: Readonly<Record<string, string>> = {
  requested: 'Requested',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
}

export const RunState = ({ state }: { state: string }): JSX.Element => (
  <span className={`run-state run-state-${state}`}>{RUN_STATES[state] ?? state}</span>
)
